import {
  ProductLine,
  ProductManagement,
  ProductManagementFields,
  SelectionPermission,
} from 'store/productManagement/reducer.types';
import { DirectSalesPricingMode } from 'components/common/types/DirectSalesCampaign.types';
import {
  DirectSalesProductTemplateChannelFilter,
  DirectSalesProductTemplateMediaFilter,
  ProductTemplateResponse,
  ProductTemplateRulesResponse,
  RuleType,
} from 'components/common/types/DirectSalesProductTemplate.types';
import { mapLocationFilter, mappedFormatFilterName } from 'components/pages/Planner/hooks/utils/transformFilters';
import { transformObjectives } from './transformObjectivesFromResponse';
import { transformCustomPoi, transformPoi } from './transformPoiFromResponse';
import { transformLocationAndFormatRules } from './transformLocationAndFormatRules';
import { transformNetworks } from './transformNetworks';
import { transformFrameListFromResponse } from './transformFrameListFromResponse';
import { transformTagsFromResponse } from './transformTagsFromResponse';
import { transformSotFromResponse } from './transformSotFromResponse';
import { transformSweepFromResponse } from './transformSweepFromResponse';
import { transformTarget } from './transformTarget';

type TransformResult = {
  newProduct: ProductManagement['newProduct'];
  productLines: ProductLine[];
};

const transformRulesToProductFields = (rules: ProductTemplateRulesResponse[]): ProductManagementFields => {
  const defaultValue = {
    allowedSet: [],
    fixedSet: [],
    rangeSet: [],
    defaultSet: [],
    behaviour: SelectionPermission.DISABLED,
  };

  const rulesMap = new Map<ProductTemplateRulesResponse['ruleType'], ProductTemplateRulesResponse>(
    rules.map((rule) => [rule.ruleType, rule]),
  );

  const getRule = (ruleType: RuleType): ProductTemplateRulesResponse =>
    rulesMap.get(ruleType) ?? { ...defaultValue, ruleType };

  const getRules = (filter: Record<string, string>): ProductTemplateRulesResponse[] =>
    rules.filter((rule) => Object.values(filter).includes(rule.ruleType));

  const mapBehaviourToPermission = (ruleType: RuleType): SelectionPermission => {
    const rule = getRule(ruleType);

    const behaviourToPermission: Record<string, SelectionPermission> = {
      FIXED: SelectionPermission.FIXED_VALUE,
      DEFAULT: SelectionPermission.DEFAULT_VALUE,
      RANGE: SelectionPermission.RANGE_VALUE,
      SET: SelectionPermission.ALLOWED_VALUES,
      ANY: SelectionPermission.ALL_OPTIONS,
    };

    return behaviourToPermission[rule.behaviour] ?? SelectionPermission.DISABLED;
  };

  const getFixedValue = <T>(ruleType: RuleType): T | undefined => {
    const { behaviour, fixedSet = [] } = getRule(ruleType);

    if (ruleType === 'ChannelFilter') return behaviour === 'FIXED' ? (fixedSet as unknown as T) : undefined;

    return behaviour === 'FIXED' ? (fixedSet[0] as unknown as T) : undefined;
  };

  const originalRange = getRule('Range');
  const originalNetworks = getRule('NetworkTarget');
  const networks = originalNetworks && transformNetworks(originalNetworks, mapBehaviourToPermission('NetworkTarget'));

  const locationRules = getRules(mapLocationFilter);
  const locations = transformLocationAndFormatRules(locationRules, mapLocationFilter);

  const formatRules = getRules(mappedFormatFilterName);
  const formats = transformLocationAndFormatRules(formatRules, mappedFormatFilterName);

  const objectivesRules = getRule('Objectives');
  const objectivesValue = transformObjectives(objectivesRules);

  const poiRules = getRule('PoiFilter');
  const poiPermission = mapBehaviourToPermission('PoiFilter');
  const poiValue = transformPoi(poiRules);

  const customPoiRules = getRule('CustomPoiFilter');
  const customPoiPermission = mapBehaviourToPermission('CustomPoiFilter');
  const customPoiValue = transformCustomPoi(customPoiRules);

  const allPoiValues = [...poiValue, ...customPoiValue];
  const effectivePoiPermission = poiPermission !== SelectionPermission.DISABLED ? poiPermission : customPoiPermission;

  const frameListRules = getRule('UnitListFilter');
  const frameListPermission = mapBehaviourToPermission('UnitListFilter');
  const frameList = transformFrameListFromResponse(frameListRules, frameListPermission);

  const tagsRules = getRule('TagFilter');
  const tagsPermission = mapBehaviourToPermission('TagFilter');
  const tags = transformTagsFromResponse(tagsRules, tagsPermission);

  const sotRule = getRule('SotTarget');
  const sotPermission = mapBehaviourToPermission('SotTarget');
  const sot = transformSotFromResponse(sotRule, sotPermission);

  const sweepRule = getRule('Sweep');
  const sweepPermission = mapBehaviourToPermission('Sweep');
  const sweep = transformSweepFromResponse(sweepRule, sweepPermission);

  const budget = transformTarget(getRule('BudgetTarget'), mapBehaviourToPermission('BudgetTarget'));
  const frames = transformTarget(getRule('FrameTarget'), mapBehaviourToPermission('FrameTarget'));
  const impressions = transformTarget(getRule('VolumeTarget'), mapBehaviourToPermission('VolumeTarget'));

  return {
    pricingMode: {
      selectionPermission: mapBehaviourToPermission('PricingMode'),
      value: getFixedValue<DirectSalesPricingMode>('PricingMode'),
    },
    range: {
      selectionPermission: mapBehaviourToPermission('Range'),
      value:
        originalRange?.behaviour === 'SET'
          ? {
              startDate: new Date(originalRange.allowedSet[0].startDate),
              endDate: new Date(originalRange.allowedSet[0].endDate),
            }
          : undefined,
    },
    mediaType: {
      selectionPermission: mapBehaviourToPermission('MediaTypeFilter'),
      value: getFixedValue<DirectSalesProductTemplateMediaFilter>('MediaTypeFilter')?.mediaType,
    },
    channels: {
      selectionPermission: mapBehaviourToPermission('ChannelFilter'),
      value: getFixedValue<DirectSalesProductTemplateChannelFilter[]>('ChannelFilter')?.map((channel) => ({
        code: channel.id,
        name: channel.name,
        dayPartGroupCode: '',
        audienceCategoryGroupCode: '',
      })),
    },
    budget,
    frames,
    impressions,
    locations,
    formats,
    networks,
    objectives: {
      selectionPermission: mapBehaviourToPermission('Objectives'),
      value: objectivesValue,
    },
    poi:
      allPoiValues.length && effectivePoiPermission !== SelectionPermission.ALL_OPTIONS
        ? {
            selectionPermission: effectivePoiPermission,
            value: allPoiValues,
          }
        : undefined,
    frameList,
    tags,
    sot,
    sweep,
  };
};

export const transformOriginalProduct = (originalProduct: ProductTemplateResponse): TransformResult => {
  const lines = originalProduct.lines ?? [];

  const productLines: ProductLine[] = lines.map((line, index) => {
    const fields = transformRulesToProductFields(line.rules);
    return {
      lineId: String(index + 1),
      name: line.lineName,
      ...fields,
    };
  });

  const firstLineFields =
    lines.length > 0 ? transformRulesToProductFields(lines[0].rules) : ({} as ProductManagementFields);

  return {
    productLines,
    newProduct: {
      id: '',
      name: originalProduct.name,
      folder: originalProduct.folder,
      ...firstLineFields,
    },
  };
};
